// A03 — Admin Users list. Add / Delete (selection) + per-row Edit.
// Reference TVP2 §9 (admin-users page): Search filter card (User Name +
// E-mail + Status) → Add/Delete action bar → table with checkbox column +
// User Name + E-mail + Role + Status + Manage (per-row pencil edit).

import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { tokens } from "@findgeeks/ui-tokens";
import { apiFetch } from "@/lib/api";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { usePagedQuery } from "@/hooks/usePagedQuery";
import { useAdminMe } from "@/hooks/useAdminMe";

interface AdminUserRow {
  id: string;
  display_name: string | null;
  email: string | null;
  role: string;
  is_active: boolean;
  created_at: string;
  role_label?: string | null;
}

export function AdminUserList() {
  const navigate = useNavigate();
  const { data: me } = useAdminMe();
  const meId = me?.profile?.id;
  const { show, ToastEl } = useToast();

  // Search inputs (local) + applied filters (sent to API).
  const [nameQ, setNameQ] = useState("");
  const [emailQ, setEmailQ] = useState("");
  const [roleQ, setRoleQ] = useState<
    "" | "Administrator" | "Event user" | "Normal User"
  >("");
  const [statusQ, setStatusQ] = useState<"" | "active" | "inactive">("");
  const [filters, setFilters] = useState<{
    name?: string;
    email?: string;
    role_label?: string;
    is_active?: string;
  }>({});

  const [selected, setSelected] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const list = usePagedQuery<AdminUserRow>("admin-users", {
    filters,
    initialPagination: { page: 0, pageSize: 10 },
  });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    setFilters({
      name: nameQ.trim() || undefined,
      email: emailQ.trim() || undefined,
      role_label: roleQ || undefined,
      is_active:
        statusQ === "active" ? "true" : statusQ === "inactive" ? "false" : undefined,
    });
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };
  const onReset = () => {
    setNameQ("");
    setEmailQ("");
    setRoleQ("");
    setStatusQ("");
    setFilters({});
    list.setPaginationModel({ ...list.paginationModel, page: 0 });
  };

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results: Array<{ id: string; ok: boolean }> = [];
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i]!;
        try {
          await apiFetch(`/v1/admin/admin-users/${id}`, { method: "DELETE" });
          results.push({ id, ok: true });
        } catch {
          results.push({ id, ok: false });
        }
        if (i < ids.length - 1) await new Promise((r) => setTimeout(r, 200));
      }
      return results;
    },
    onSuccess: (results) => {
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      show(
        failCount === 0
          ? `Deleted ${okCount} of ${results.length}`
          : `Deleted ${okCount} of ${results.length}; ${failCount} failed`,
        failCount === 0 ? "success" : "warning",
      );
      setSelected([]);
      setDeleteOpen(false);
      list.invalidate();
    },
  });

  const columns: DataTableColumn<AdminUserRow>[] = [
    {
      field: "display_name",
      headerName: "User Name",
      render: (r) => r.display_name ?? "-",
    },
    { field: "email", headerName: "E-mail", render: (r) => r.email ?? "-" },
    {
      field: "role_label",
      headerName: "Role",
      render: (r) => r.role_label ?? r.role ?? "-",
    },
    {
      field: "is_active",
      headerName: "Status",
      render: (r) => (r.is_active ? "Active" : "Inactive"),
    },
  ];

  // Self-protection: cannot delete yourself.
  const deletableSelected = selected.filter((id) => id !== meId);

  // Reference TVP2 §9 uses Bootstrap-style action button colors (NOT the
  // page's teal accent): Search = blue, Add = green, Reset/Delete = grey.
  // White text on all; flat (no boxShadow) to match the reference look.
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const searchBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
  };
  const addBtn = {
    ...baseBtn,
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#E8E8E8",
      color: "rgba(255,255,255,0.85)",
    },
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Search filter card */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
        }}
      >
        <Tabs
          value="search"
          sx={{
            minHeight: 38,
            borderBottom: `1px solid ${tokens.colors.admin.border}`,
            "& .MuiTabs-indicator": {
              backgroundColor: tokens.colors.admin.tabActive,
              height: 2,
            },
            "& .MuiTab-root": {
              minHeight: 38,
              textTransform: "none",
              fontSize: 13,
              color: tokens.colors.admin.textHeading,
              "& .MuiTab-iconWrapper": {
                marginRight: 0.5,
                marginBottom: "0 !important",
                "& svg": { fontSize: 16 },
              },
            },
          }}
        >
          <Tab
            value="search"
            iconPosition="start"
            icon={<SearchIcon />}
            label="Search"
          />
        </Tabs>

        <Box component="form" onSubmit={onSearch} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="User Name"
              variant="standard"
              fullWidth
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
            />
            <TextField
              label="E-mail"
              variant="standard"
              fullWidth
              value={emailQ}
              onChange={(e) => setEmailQ(e.target.value)}
            />
            <TextField
              label="Status"
              variant="standard"
              select
              fullWidth
              value={statusQ}
              onChange={(e) => setStatusQ(e.target.value as typeof statusQ)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              label="Role"
              variant="standard"
              select
              fullWidth
              value={roleQ}
              onChange={(e) => setRoleQ(e.target.value as typeof roleQ)}
            >
              <MenuItem value="Administrator">Administrator</MenuItem>
              <MenuItem value="Event user">Event User</MenuItem>
              <MenuItem value="Normal User">Normal User</MenuItem>
            </TextField>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" sx={searchBtn}>
              Search
            </Button>
            <Button type="button" variant="contained" onClick={onReset} sx={greyBtn}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Action bar + table — single bordered card per reference. */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${tokens.colors.admin.border}`,
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ px: 3, pt: 3, pb: 2 }}>
          <Button
            variant="contained"
            sx={addBtn}
            onClick={() => navigate("/app/admin-users/new")}
          >
            Add
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            disabled={deletableSelected.length === 0 || deleteMutation.isPending}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Stack>

        {/* Inner padding around the table so rows + headers don't sit flush
            against the card edges — matches the reference's breathing room. */}
        <Box sx={{ px: 3, pb: 2 }}>
        <DataTable
          columns={columns}
          rows={list.data?.items ?? []}
          rowCount={list.data?.total ?? 0}
          paginationModel={list.paginationModel}
          onPaginationModelChange={list.setPaginationModel}
          getRowId={(r) => r.id}
          checkboxSelection
          selectedIds={selected}
          onSelectionChange={setSelected}
          loading={list.isLoading}
          bordered={false}
          rowActions={(row) => (
            <Stack direction="row" spacing={0}>
              <IconButton
                size="small"
                aria-label="Edit"
                onClick={() => navigate(`/app/admin-users/${row.id}`)}
                sx={{ color: tokens.colors.admin.textMuted }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              {row.id !== meId && (
                <IconButton
                  size="small"
                  aria-label="Delete"
                  onClick={() => {
                    setSelected([row.id]);
                    setDeleteOpen(true);
                  }}
                  sx={{ color: tokens.colors.admin.textMuted }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          )}
        />
        </Box>
      </Paper>

      <ConfirmModal
        open={deleteOpen}
        title="Delete admins"
        body={`About to delete ${deletableSelected.length} admin user${deletableSelected.length === 1 ? "" : "s"}. This soft-disables the account(s). Continue?`}
        confirmLabel="Delete"
        danger
        pending={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(deletableSelected)}
        onCancel={() => setDeleteOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
